import { useCallback, useEffect, useRef, useState } from 'react';

// Values are stored as JSON where possible, but plain strings were written
// unquoted historically, so a parse failure falls back to the raw string.
const tryParse = (value: string | null): unknown => {
  try {
    return JSON.parse(value as string);
  } catch {
    return value;
  }
};

const storageUndefined =
  typeof localStorage === 'undefined' || localStorage === null;

const readStorage = storageUndefined
  ? async () => null
  : async (key: string) => tryParse(localStorage.getItem(key));

const writeStorage = storageUndefined
  ? () => {}
  : (key: string, value: unknown) =>
      localStorage.setItem(
        key,
        typeof value === 'object' ? JSON.stringify(value) : `${value}`
      );

const removeStorage = storageUndefined
  ? () => {}
  : (key: string) => localStorage.removeItem(key);

const dispatchEvent = (
  key: string,
  newValue: unknown,
  storageArea: Storage,
  instance: object
) =>
  window.dispatchEvent(
    new CustomEvent('sameContextStorage', {
      detail: { instance, storageArea, key, newValue },
    })
  );

// localStorage holds untyped JSON; the caller declares the shape via T, so this
// is the one place that assertion is made. Module-level so it does not become an
// effect dependency below.
const fromStorage = <T,>(stored: unknown) => stored as T;

const useLocalStorage = <T,>(key: string, defaultValue: T) => {
  const [{ value, initialising, received }, setState] = useState<{
    value: T;
    initialising: boolean;
    received: boolean | null;
  }>({
    value: defaultValue,
    initialising: true,
    received: null,
  });

  const defaultValueRef = useRef(defaultValue);
  const valueRef = useRef(value);

  useEffect(() => {
    const initialise = async () => {
      const storedValue = await readStorage(key);

      const nextValue =
        storedValue === null
          ? defaultValueRef.current
          : fromStorage<T>(storedValue);

      valueRef.current = nextValue;
      setState({
        value: nextValue,
        initialising: false,
        received: null,
      });
    };

    initialise();
  }, [key]);

  // The instance is just an arbitrary object whose identity is used to
  // determine whether the event was dispatched from this instance of the
  // hook or a different instance within the same browser tab.
  const instanceRef = useRef({});

  useEffect(() => {
    const onStorage = ({
      storageArea,
      key: eventKey,
      newValue,
    }: {
      storageArea: Storage | null;
      key: string | null;
      newValue: string | null;
    }) => {
      if (storageArea === localStorage && eventKey === key) {
        const parsed = tryParse(newValue);

        if (parsed === null) {
          return;
        }

        const nextValue = fromStorage<T>(parsed);

        valueRef.current = nextValue;

        setState({
          value: nextValue,
          initialising: false,
          received: true,
        });
      }
    };

    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [key]);

  useEffect(() => {
    const onSameContextStorage = ({
      detail: { instance, storageArea, key: eventKey, newValue: nextValue },
    }: {
      detail: {
        instance: object;
        storageArea: Storage;
        key: string;
        newValue: unknown;
      };
    }) => {
      if (storageArea === localStorage && eventKey === key) {
        const value =
          nextValue === null
            ? defaultValueRef.current
            : fromStorage<T>(nextValue);

        valueRef.current = value;
        setState({
          value,
          initialising: false,
          // All instances are equal but some are more equal than others.
          received: instance !== instanceRef.current,
        });
      }
    };

    window.addEventListener(
      'sameContextStorage',
      onSameContextStorage as unknown as EventListener
    );
    return () => {
      window.removeEventListener(
        'sameContextStorage',
        onSameContextStorage as unknown as EventListener
      );
    };
  }, [key]);

  const setValue = useCallback(
    (arg: T | ((prev: T) => T)) => {
      const nextValue =
        arg instanceof Function
          ? (arg as (prev: T) => T)(valueRef.current)
          : arg;
      writeStorage(key, nextValue);
      dispatchEvent(key, nextValue, localStorage, instanceRef.current);
    },
    [key]
  );

  const removeValue = useCallback(() => {
    removeStorage(key);
    dispatchEvent(key, null, localStorage, instanceRef.current);
  }, [key]);

  // A tuple, not an array, so destructuring gives each position its own type
  // rather than the union of all five.
  return [value, setValue, removeValue, initialising, received] as const;
};

export default useLocalStorage;
